import {
  verifyOpenPencilComponentOntologyCandidateV2,
  type OpenPencilComponentOntologyCandidateInputV2,
  type OpenPencilComponentOntologyCandidateV2,
} from "./component-ontology-candidate-v2.ts";
import type { CanonicalComponentRole } from "./component-role-registry.ts";
import {
  verifyDesignTokenOntologyCandidate,
  type DesignTokenOntologyCandidateInputV2,
  type DesignTokenOntologyCandidateV2,
  type DesignTokenType,
} from "./design-token-ontology-candidate-v2.ts";
import {
  buildOntologyCoverageMatrixV2Internal,
  canonicalCoverageJsonV2,
  coverageSha256V2,
} from "./coverage-matrix-v2-internal.ts";

const FOUNDATION_COMPONENT_WAVE1_COVERAGE_PROFILE_AUTHORITY = deepFreeze({
  profileId: "cogo.foundation-component-wave1-coverage.v1",
  ontologyScope: "platform-neutral",
  cohortSourceArtifactHashes: [
    "sha256:147564c22a961d69a2ed2615901275489bb2c6746737b098904f66884f2f0f17",
    "sha256:1c9a4f45828396bbd6221c63d39fb0787114ec2cc644b8603d298e9064cac36c",
    "sha256:d224c237f870c74c8c456ffd365c95145272e3cd5d457a463b6d59af6cbfaa53",
  ],
  requiredCandidateKinds: ["design-token", "component"],
  mandatoryFoundationRoles: [
    "color",
    "typography",
    "spacing",
    "sizing",
    "radius",
    "border",
    "elevation",
    "opacity",
  ],
  mandatoryComponentRoles: [
    "button",
    "input",
    "select",
    "checkbox",
    "radio",
    "switch",
    "slider",
    "tabs",
    "dialog",
    "drawer",
    "tooltip",
    "popover",
    "menu",
    "navigation",
    "breadcrumb",
    "pagination",
    "table",
    "card",
    "badge",
    "avatar",
    "alert",
    "progress",
    "skeleton",
  ],
  coveredIndependentSourceThreshold: 2,
  redundantIndependentSourceThreshold: 3,
} as const);

export const FOUNDATION_COMPONENT_WAVE1_COVERAGE_PROFILE =
  FOUNDATION_COMPONENT_WAVE1_COVERAGE_PROFILE_AUTHORITY;

export type FoundationCoverageRole =
  (typeof FOUNDATION_COMPONENT_WAVE1_COVERAGE_PROFILE.mandatoryFoundationRoles)[number];
export type CoverageSourceArtifactHash =
  (typeof FOUNDATION_COMPONENT_WAVE1_COVERAGE_PROFILE.cohortSourceArtifactHashes)[number];
type Sha256 = `sha256:${string}`;

declare const coverageReadbackMemoizerBrand: unique symbol;
export type CoverageReadbackMemoizerBrand = {
  readonly [coverageReadbackMemoizerBrand]: true;
};
export type CoverageReadbackMemoizer = (() => unknown) &
  CoverageReadbackMemoizerBrand;

const brandedMemoizers = new WeakSet<Function>();

export function createCoverageReadbackMemoizer(
  loadOnce: () => unknown,
): CoverageReadbackMemoizer {
  if (typeof loadOnce !== "function") {
    throw new Error("COVERAGE_READBACK_LOADER_REQUIRED");
  }
  let settled = false;
  let failed = false;
  let returned: unknown;
  let thrown: unknown;
  const wrapper = (() => {
    if (!settled) {
      try {
        returned = loadOnce();
      } catch (error) {
        failed = true;
        thrown = error;
      }
      settled = true;
    }
    if (failed) throw thrown;
    return returned;
  }) as CoverageReadbackMemoizer;
  brandedMemoizers.add(wrapper);
  return wrapper;
}

export function isBrandedCoverageReadbackMemoizerV2(
  loader: () => unknown,
): boolean {
  return brandedMemoizers.has(loader);
}

export interface VerifiedTokenCoverageInputV2 {
  kind: "design-token";
  input: DesignTokenOntologyCandidateInputV2;
  candidate: DesignTokenOntologyCandidateV2;
}

export interface VerifiedComponentCoverageInputV2 {
  kind: "component";
  input: OpenPencilComponentOntologyCandidateInputV2;
  candidate: OpenPencilComponentOntologyCandidateV2;
}

export interface BuildOntologyCoverageMatrixV2Input {
  profileId: typeof FOUNDATION_COMPONENT_WAVE1_COVERAGE_PROFILE.profileId;
  candidates: Array<
    VerifiedTokenCoverageInputV2 | VerifiedComponentCoverageInputV2
  >;
}

type CandidateEvidence = {
  kind: "design-token" | "component";
  sourceArtifactHash: Sha256;
  candidateContentHash: Sha256;
  semanticVerifier:
    | "@cogo/source-openpencil#verifyDesignTokenOntologyCandidate"
    | "@cogo/source-openpencil#verifyOpenPencilComponentOntologyCandidateV2";
  verified: true;
};

type CoverageEntry = {
  level: "foundation" | "component";
  canonicalRole: FoundationCoverageRole | CanonicalComponentRole;
  mandatory: true;
  shapeSignatures: Sha256[];
  sourceArtifactHashes: Sha256[];
  independentObservationCount: number;
  status: "covered" | "single-source" | "gap";
};

export interface FigmaCommunityOntologyCoverageMatrixV2 {
  schemaVersion: "cogo.figma-community-ontology-coverage-matrix.v2";
  profileId: typeof FOUNDATION_COMPONENT_WAVE1_COVERAGE_PROFILE.profileId;
  ontologyScope: typeof FOUNDATION_COMPONENT_WAVE1_COVERAGE_PROFILE.ontologyScope;
  cohortSourceArtifactHashes: Sha256[];
  candidateEvidence: CandidateEvidence[];
  entries: CoverageEntry[];
  evidenceTotals: {
    tokenGapCount: number;
    unmappedComponentCount: number;
    ambiguousComponentCount: number;
  };
  saturation: {
    mandatoryCategoryCount: number;
    coveredCategoryCount: number;
    criticalGapCount: number;
    redundantIndependentSourceCount: number;
    saturated: boolean;
  };
  authority: {
    evidenceOnly: true;
    generationEligible: false;
  };
  contentHash: Sha256;
}

type JsonRecord = Record<string, unknown>;
type CandidateInput =
  | VerifiedTokenCoverageInputV2
  | VerifiedComponentCoverageInputV2;
type CandidateSnapshot =
  | {
      kind: "design-token";
      input: DesignTokenOntologyCandidateInputV2;
      candidate: Readonly<DesignTokenOntologyCandidateV2>;
      candidateBytes: string;
      candidateContentHash: Sha256;
    }
  | {
      kind: "component";
      input: OpenPencilComponentOntologyCandidateInputV2;
      candidate: Readonly<OpenPencilComponentOntologyCandidateV2>;
      candidateBytes: string;
      candidateContentHash: Sha256;
    };
type Projection = {
  level: "foundation" | "component";
  canonicalRole: FoundationCoverageRole | CanonicalComponentRole;
  shapeSignature: Sha256;
  sourceArtifactHash: Sha256;
};

function isRecord(value: unknown): value is JsonRecord {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

function canonicalJson(value: unknown): string {
  return canonicalCoverageJsonV2(value);
}

function canonicalSort<T>(values: readonly T[]): T[] {
  return [...values].sort((left, right) => {
    const a = canonicalJson(left);
    const b = canonicalJson(right);
    return a === b ? 0 : a < b ? -1 : 1;
  });
}

function sortedUnique<T>(values: readonly T[]): T[] {
  const output: T[] = [];
  let previous: string | undefined;
  for (const value of canonicalSort(values)) {
    const bytes = canonicalJson(value);
    if (bytes !== previous) output.push(value);
    previous = bytes;
  }
  return output;
}

function sha256(value: unknown): Sha256 {
  return coverageSha256V2(value);
}

function deepFreeze<T>(value: T, seen = new WeakSet<object>()): Readonly<T> {
  if (value === null || typeof value !== "object" || seen.has(value)) {
    return value;
  }
  if (ArrayBuffer.isView(value)) {
    return value;
  }
  seen.add(value);
  for (const child of Object.values(value as JsonRecord)) {
    deepFreeze(child, seen);
  }
  return Object.freeze(value);
}

function snapshotVerifierInput<T extends { loadLocalReadback: () => unknown }>(
  input: T,
): T {
  if (!input || typeof input.loadLocalReadback !== "function") {
    throw new Error("COVERAGE_VERIFIER_INPUT_INVALID");
  }
  const { loadLocalReadback, ...cloneable } = input;
  const snapshot = deepFreeze(structuredClone(cloneable));
  return Object.freeze({
    ...snapshot,
    loadLocalReadback,
  }) as T;
}

function snapshotCandidate(entry: CandidateInput): CandidateSnapshot {
  if (entry.kind === "design-token") {
    const candidate = deepFreeze(structuredClone(entry.candidate));
    return {
      kind: entry.kind,
      input: snapshotVerifierInput(entry.input),
      candidate,
      candidateBytes: canonicalJson(candidate),
      candidateContentHash: sha256(candidate),
    };
  }
  const candidate = deepFreeze(structuredClone(entry.candidate));
  return {
    kind: entry.kind,
    input: snapshotVerifierInput(entry.input),
    candidate,
    candidateBytes: canonicalJson(candidate),
    candidateContentHash: sha256(candidate),
  };
}

function candidateSourceHash(snapshot: CandidateSnapshot): Sha256 {
  const value = snapshot.candidate.sourceRefs?.sourceArtifactHash;
  if (typeof value !== "string") {
    throw new Error("COVERAGE_CANDIDATE_SOURCE_REFS_INVALID");
  }
  return value;
}

function sourceRefsBytes(snapshot: CandidateSnapshot): string {
  return canonicalJson(snapshot.candidate.sourceRefs);
}

function assertFixedCohort(snapshots: readonly CandidateSnapshot[]): void {
  if (snapshots.length !== 6) {
    throw new Error("COVERAGE_CANDIDATE_COHORT_INVALID");
  }
  const expected = new Set<string>(
    FOUNDATION_COMPONENT_WAVE1_COVERAGE_PROFILE_AUTHORITY.cohortSourceArtifactHashes,
  );
  const groups = new Map<string, CandidateSnapshot[]>();
  for (const snapshot of snapshots) {
    const sourceHash = candidateSourceHash(snapshot);
    if (!expected.has(sourceHash)) {
      throw new Error("COVERAGE_CANDIDATE_COHORT_INVALID");
    }
    groups.set(sourceHash, [...(groups.get(sourceHash) ?? []), snapshot]);
  }
  if (
    groups.size !== expected.size ||
    [...expected].some((sourceHash) => !groups.has(sourceHash))
  ) {
    throw new Error("COVERAGE_CANDIDATE_COHORT_INVALID");
  }
  for (const group of groups.values()) {
    if (
      group.length !== 2 ||
      group.filter((entry) => entry.kind === "design-token").length !== 1 ||
      group.filter((entry) => entry.kind === "component").length !== 1
    ) {
      throw new Error("COVERAGE_CANDIDATE_COHORT_INVALID");
    }
    const [first, second] = group;
    if (sourceRefsBytes(first!) !== sourceRefsBytes(second!)) {
      throw new Error("COVERAGE_SOURCE_REFS_MISMATCH");
    }
    const firstLoader = first!.input.loadLocalReadback;
    const secondLoader = second!.input.loadLocalReadback;
    if (
      !brandedMemoizers.has(firstLoader) ||
      !brandedMemoizers.has(secondLoader)
    ) {
      throw new Error("COVERAGE_READBACK_MEMOIZER_REQUIRED");
    }
    if (firstLoader !== secondLoader) {
      throw new Error("COVERAGE_READBACK_MEMOIZER_MISMATCH");
    }
  }
}

async function semanticallyVerify(
  snapshot: CandidateSnapshot,
): Promise<CandidateEvidence> {
  const verification =
    snapshot.kind === "design-token"
      ? await verifyDesignTokenOntologyCandidate(
          snapshot.input,
          snapshot.candidate,
        )
      : await verifyOpenPencilComponentOntologyCandidateV2(
          snapshot.input,
          snapshot.candidate,
        );
  if (!verification.valid) {
    throw new Error(
      `COVERAGE_CANDIDATE_SEMANTIC_VERIFICATION_FAILED:${verification.issues.join("|")}`,
    );
  }
  if (
    snapshot.candidateBytes !== canonicalJson(snapshot.candidate) ||
    snapshot.candidateContentHash !== sha256(snapshot.candidate)
  ) {
    throw new Error("COVERAGE_CANDIDATE_SNAPSHOT_MUTATED");
  }
  return {
    kind: snapshot.kind,
    sourceArtifactHash: candidateSourceHash(snapshot),
    candidateContentHash: snapshot.candidateContentHash,
    semanticVerifier:
      snapshot.kind === "design-token"
        ? "@cogo/source-openpencil#verifyDesignTokenOntologyCandidate"
        : "@cogo/source-openpencil#verifyOpenPencilComponentOntologyCandidateV2",
    verified: true,
  };
}

function foundationProjections(
  snapshot: Extract<CandidateSnapshot, { kind: "design-token" }>,
): Projection[] {
  const sourceArtifactHash = candidateSourceHash(snapshot);
  return snapshot.candidate.tokenRoles
    .filter(
      (
        token,
      ): token is typeof token & {
        canonicalRole: FoundationCoverageRole;
        tokenType: DesignTokenType;
      } =>
        token.canonicalRole !== "unmapped" &&
        FOUNDATION_COMPONENT_WAVE1_COVERAGE_PROFILE_AUTHORITY.mandatoryFoundationRoles.includes(
          token.canonicalRole as FoundationCoverageRole,
        ),
    )
    .map((token) => ({
      level: "foundation" as const,
      canonicalRole: token.canonicalRole,
      shapeSignature: sha256([
        "cogo.coverage.foundation-shape.v2",
        token.canonicalRole,
        token.tokenType,
      ]),
      sourceArtifactHash,
    }));
}

function componentProjections(
  snapshot: Extract<CandidateSnapshot, { kind: "component" }>,
): Projection[] {
  const sourceArtifactHash = candidateSourceHash(snapshot);
  return snapshot.candidate.designations
    .filter(
      (
        designation,
      ): designation is typeof designation & {
        canonicalRole: CanonicalComponentRole;
        disposition: "candidate";
      } =>
        designation.disposition === "candidate" &&
        designation.canonicalRole !== null &&
        FOUNDATION_COMPONENT_WAVE1_COVERAGE_PROFILE_AUTHORITY.mandatoryComponentRoles.includes(
          designation.canonicalRole,
        ),
    )
    .map((designation) => ({
      level: "component" as const,
      canonicalRole: designation.canonicalRole,
      shapeSignature: sha256([
        "cogo.coverage.component-shape.v2",
        designation.canonicalRole,
        designation.observedVariantCount,
        designation.propertyAxisSemanticRefs.length,
      ]),
      sourceArtifactHash,
    }));
}

function projectionKey(projection: Projection): string {
  return canonicalJson([
    projection.level,
    projection.canonicalRole,
    projection.shapeSignature,
  ]);
}

function categoryKey(
  level: CoverageEntry["level"],
  role: CoverageEntry["canonicalRole"],
): string {
  return canonicalJson([level, role]);
}

function coverageEntries(projections: readonly Projection[]): CoverageEntry[] {
  const categories = [
    ...FOUNDATION_COMPONENT_WAVE1_COVERAGE_PROFILE_AUTHORITY.mandatoryFoundationRoles.map(
      (canonicalRole) => ({
        level: "foundation" as const,
        canonicalRole,
      }),
    ),
    ...FOUNDATION_COMPONENT_WAVE1_COVERAGE_PROFILE_AUTHORITY.mandatoryComponentRoles.map(
      (canonicalRole) => ({
        level: "component" as const,
        canonicalRole,
      }),
    ),
  ];
  return canonicalSort(
    categories.map(({ level, canonicalRole }) => {
      const matches = projections.filter(
        (projection) =>
          categoryKey(projection.level, projection.canonicalRole) ===
          categoryKey(level, canonicalRole),
      );
      const sourceArtifactHashes = sortedUnique(
        matches.map((projection) => projection.sourceArtifactHash),
      );
      const independentObservationCount = sourceArtifactHashes.length;
      return {
        level,
        canonicalRole,
        mandatory: true as const,
        shapeSignatures: sortedUnique(
          matches.map((projection) => projection.shapeSignature),
        ),
        sourceArtifactHashes,
        independentObservationCount,
        status:
          independentObservationCount === 0
            ? ("gap" as const)
            : independentObservationCount === 1
              ? ("single-source" as const)
              : ("covered" as const),
      };
    }),
  );
}

function redundantSourceCount(projections: readonly Projection[]): number {
  const unique = sortedUnique(projections);
  return FOUNDATION_COMPONENT_WAVE1_COVERAGE_PROFILE_AUTHORITY.cohortSourceArtifactHashes.filter(
    (sourceArtifactHash) => {
      const own = unique.filter(
        (projection) => projection.sourceArtifactHash === sourceArtifactHash,
      );
      if (
        !own.some((projection) => projection.level === "foundation") ||
        !own.some((projection) => projection.level === "component")
      ) {
        return false;
      }
      return own.every((projection) =>
        unique.some(
          (other) =>
            other.sourceArtifactHash !== sourceArtifactHash &&
            projectionKey(other) === projectionKey(projection),
        ),
      );
    },
  ).length;
}

export async function buildOntologyCoverageMatrixV2(
  input: BuildOntologyCoverageMatrixV2Input,
): Promise<FigmaCommunityOntologyCoverageMatrixV2> {
  if (
    !input ||
    input.profileId !==
      FOUNDATION_COMPONENT_WAVE1_COVERAGE_PROFILE_AUTHORITY.profileId
  ) {
    throw new Error("COVERAGE_PROFILE_ID_INVALID");
  }
  if (!Array.isArray(input.candidates)) {
    throw new Error("COVERAGE_CANDIDATE_COHORT_INVALID");
  }
  return buildOntologyCoverageMatrixV2Internal({
    profile: FOUNDATION_COMPONENT_WAVE1_COVERAGE_PROFILE_AUTHORITY,
    candidates: input.candidates,
    isReadbackMemoizer: isBrandedCoverageReadbackMemoizerV2,
    verifyCandidate: (candidate) =>
      candidate.kind === "design-token"
        ? verifyDesignTokenOntologyCandidate(
            candidate.input,
            candidate.candidate,
          )
        : verifyOpenPencilComponentOntologyCandidateV2(
            candidate.input,
            candidate.candidate,
          ),
  });
}

export async function verifyOntologyCoverageMatrixV2(
  input: BuildOntologyCoverageMatrixV2Input,
  matrix: unknown,
): Promise<{ valid: boolean; issues: string[] }> {
  try {
    const expected = await buildOntologyCoverageMatrixV2(input);
    if (canonicalJson(expected) !== canonicalJson(matrix)) {
      return {
        valid: false,
        issues: ["COVERAGE_MATRIX_RECOMPUTATION_MISMATCH"],
      };
    }
    return { valid: true, issues: [] };
  } catch (error) {
    return {
      valid: false,
      issues: [
        error instanceof Error
          ? error.message
          : "COVERAGE_MATRIX_VERIFICATION_FAILED",
      ],
    };
  }
}
